<script setup lang="ts">
import { useDisplay } from 'vuetify';
import { computed, ref, watch } from 'vue';
import type { Series } from '@ilteducation/content-registry-types';
import useLocale from './helpers/use-locale';

type Emits = {
  (e: 'on-card-click'): void;
  (e: 'on-card-select'): void;
  (e: 'is-hovering', isHovering: boolean): void;
};
const emit = defineEmits<Emits>();
type Props = {
  type?: 'image' | 'icon';
  hasHover?: boolean;
  selected?: boolean;
  contentCardWidth?: string | number;
  contentCardHeight?: string | number;
  hasStack?: boolean;
  imageHeight: string | number;
  isRounded?: boolean;
  title?: string;
  authors?: string;
  serie?: Series;
  overflowHidden?: boolean;
  hoverRoundedClass?: string;
};
const props = withDefaults(defineProps<Props>(), {
  type: 'image',
  hasHover: true,
  selected: false,
  contentCardWidth: 128,
  contentCardHeight: 180,
  imageHeight: 180,
  isRounded: true,
  overflowHidden: true,
  hoverRoundedClass: 'rounded-lg',
});

const { platform } = useDisplay();
const { t } = useLocale();

const isHovering = ref(false);

const isTouchDevice = computed(() => platform.value.touch);

const tooltipContent = computed(() => {
  if (props.serie && props.serie.readingOrder) {
    return `${t('components.title')}: ${props.title} \n${t('components.author')}: ${props.authors} \n${t(
      'label_serie_name',
    )}: ${props.serie.name} \n${t('label_serie_part')}: ${props.serie.readingOrder}`;
  } else if (props.serie) {
    return `${t('components.title')}: ${props.title} \n${t('components.author')}: ${props.authors} \n${t(
      'label_serie_name',
    )}: ${props.serie.name}`;
  } else if (props.title) {
    return `${t('components.title')}: ${props.title} \n${t('components.author')}: ${props.authors}`;
  }
  return null;
});

watch(isHovering, (value) => {
  emit('is-hovering', value);
});
</script>

<template>
  <v-card
    variant="flat"
    class="content-hover-card rounded-0"
    :class="{
      'd-flex': true,
      'flex-column': true,
      'justify-end': true,
      'align-center': true,
    }"
    :ripple="false"
    @mouseenter="isTouchDevice ? () => {} : (isHovering = true)"
    @mouseleave="isHovering = false"
    @blur="isHovering = false"
    @focus="isHovering = true"
    @touch="isHovering = false"
    @click="emit('on-card-click')"
    @keydown.enter="emit('on-card-click')"
    @keydown.space="emit('on-card-select')"
  >
    <v-card-item
      class="content-card-item position-relative pa-0"
      :class="{
        'selected-content': selected,
        'content-card-image': type === 'image',
        'content-card-icon': type === 'icon',
        [`elevation-${selected ? 12 : isHovering ? 8 : 4}`]: type === 'icon',
      }"
      :style="`width: ${contentCardWidth}px; height: ${contentCardHeight}px;`"
    >
      <div
        class="content-card-item-images"
        :class="{
          'content-hover-card-zoom': hasHover,
          [`${hoverRoundedClass}`]: isRounded,
          'overflow-hidden-on-hover': overflowHidden,
        }"
        :style="`height: ${imageHeight ?? contentCardHeight}`"
      >
        <slot name="coverFeatures"></slot>
        <slot name="image"></slot>
        <div class="content-card-stack">
          <slot name="stack"></slot>
        </div>
      </div>
      <v-tooltip
        v-if="tooltipContent"
        :disabled="isTouchDevice"
        location="bottom"
        activator="parent"
        :text="tooltipContent"
        class="tooltip"
      />
    </v-card-item>
    <div class="content-card-title pa-0" :style="{ maxWidth: contentCardWidth + 'px' }">
      <slot name="title"></slot>
    </div>
  </v-card>
</template>

<style scoped lang="scss">
.content-hover-card {
  height: fit-content !important;
  width: fit-content !important;
  overflow: visible;
  background: none;
}

.content-card-stack {
  position: absolute;
  top: 0;
  left: 0;
  z-index: -1;
}

:deep(.v-card__overlay) {
  background-color: transparent !important;
}

.content-card-item {
  &-images {
    position: relative;
    overflow: visible;
  }
}
.content-hover-card-zoom {
  transition: outline-color 0.3s ease-out;
  outline: solid 3px transparent !important;
  outline-offset: 2px;
}

:deep(.v-card-item__content) {
  overflow: visible !important;
}

.content-card-item .content-hover-card-zoom {
  transition: ease-in-out 0.3s all;

  &.content-card-icon {
    display: inline-block;
    background-color: transparent;
    height: fit-content;
    border-radius: 50%;
  }

  &:focus-visible {
    transform: scale(1.08);
    outline: none !important;
  }

  /* Styles for devices with hover capability */
  @media (hover: hover) {
    &:hover,
    &:focus-within:not(:hover) {
      transform: scale(1.08);
      transition:
        transform 0.3s ease-in-out,
        outline-color 0.3s ease-in;
      outline: solid 3px rgb(var(--v-theme-primary)) !important;

      .content-card-stack {
        display: none;
      }
    }

    &:active,
    &:focus,
    &:focus:not(:hover) {
      transform: scale(0.95);
      transition:
        transform 0.3s ease-in-out,
        outline-color 0.3s ease-in;
      outline: solid 3px rgb(var(--v-theme-primary)) !important;
    }
  }

  /* Styles for devices without hover (e.g., touchscreens) */
  @media (hover: none) {
    &:focus-within {
      transform: scale(1.05);
      transition:
        transform 0.3s ease-in-out,
        outline-color 0.3s ease-in;
      outline: solid 3px rgb(var(--v-theme-primary)) !important;
    }

    &:active {
      transform: scale(0.9);
      transition:
        transform 0.3s ease-in-out,
        outline-color 0.3s ease-in;
      outline: solid 3px rgb(var(--v-theme-primary)) !important;
    }
  }

  /* Dynamically controlled overflow */
  &.overflow-hidden-on-hover {
    @media (hover: hover) {
      &:hover,
      &:focus-within:not(:hover) {
        overflow: hidden;
      }

      &:active,
      &:focus,
      &:focus:not(:hover) {
        overflow: hidden;
      }
    }

    @media (hover: none) {
      &:focus-within {
        overflow: hidden;
      }

      &:active {
        overflow: hidden;
      }
    }
  }
}
.tooltip ::v-deep(.v-overlay__content) {
  white-space: pre-wrap;
  word-wrap: break-word;
  transform: translateY(5px);
  opacity: 0.8;
}
</style>
